import React from 'react';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import { Link } from 'react-router-dom';
import { useUser } from './UserContext';
import WelcomeCard from './WelcomeCard';

const Privacy = () => {
  const { user } = useUser();

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      position: 'relative',
      flex: 1,
      paddingTop: '50px',
      paddingBottom: '50px',
    }}>
      <div style={{position: 'absolute', left: '0px', top: '0px', width:'100%', opacity: '10%', pointerEvents: 'none' }}>
        <img src='/mascot.png' alt=''/>
      </div>

      {!user && (
        <WelcomeCard />
      )}

      <Paper
        elevation={6}
        sx={{
          padding: '40px',
          maxWidth: '800px',
          width: '100%',
          marginBottom: '50px',
          backgroundColor: (theme) => theme.palette.background.paper,
          color: (theme) => theme.palette.text.primary,
        }}
      >
        <Typography variant="h3" gutterBottom sx={{ textAlign: 'center', marginBottom: '30px' }}>
          Privacy Policy
        </Typography>

        <Typography variant="body1" paragraph>
          Snapperish is a platform owned and operated by Five Pack Creative, Inc., a Texas corporation. Five Pack Creative, Inc. is the data controller for personal information collected through Snapperish.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ marginTop: '30px' }}>
          Geographic Scope
        </Typography>
        <Typography variant="body1" paragraph>
          Snapperish is intended for use by residents of the United States. We do not actively market to or solicit users in the European Union or European Economic Area. If you are located in the EU or EEA, please be aware that Snapperish is not currently designed to meet the requirements of the General Data Protection Regulation (GDPR), and we encourage you not to use this service until such compliance is established.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ marginTop: '30px' }}>
          Information We Collect
        </Typography>
        <Typography variant="body1" paragraph>
          When you use Snapperish, we collect the following types of information:
        </Typography>
        <Box component="ul" sx={{ paddingLeft: '20px', marginBottom: '20px' }}>
          <Typography component="li" variant="body1" sx={{ marginBottom: '8px' }}>
            <strong>Account Information:</strong> Email address, full name, and login credentials (stored as a hash)
          </Typography>
          <Typography component="li" variant="body1" sx={{ marginBottom: '8px' }}>
            <strong>Photos and Metadata:</strong> Images you upload, including titles, location information manually entered by the user (such as city or region), and photographer names. Snapperish does not collect GPS or device-embedded location data from your photos — EXIF location metadata is automatically stripped upon upload.
          </Typography>
          <Typography component="li" variant="body1" sx={{ marginBottom: '8px' }}>
            <strong>Club Information:</strong> Club membership details and contest participation data
          </Typography>
          <Typography component="li" variant="body1" sx={{ marginBottom: '8px' }}>
            <strong>Usage Data:</strong> Information about how you interact with our platform
          </Typography>
          <Typography component="li" variant="body1" sx={{ marginBottom: '8px' }}>
            <strong>Communication Data:</strong> Messages sent through our contact forms or support channels
          </Typography>
        </Box>

        <Typography variant="h5" gutterBottom sx={{ marginTop: '30px' }}>
          How We Use Your Information
        </Typography>
        <Typography variant="body1" paragraph>
          We use your information for the following purposes:
        </Typography>
        <Box component="ul" sx={{ paddingLeft: '20px', marginBottom: '20px' }}>
          <Typography component="li" variant="body1" sx={{ marginBottom: '8px' }}>
            Providing and maintaining the Snapperish platform and contest services
          </Typography>
          <Typography component="li" variant="body1" sx={{ marginBottom: '8px' }}>
            Facilitating photo contests within your club or organization
          </Typography>
          <Typography component="li" variant="body1" sx={{ marginBottom: '8px' }}>
            Enabling voting and contest management features
          </Typography>
          <Typography component="li" variant="body1" sx={{ marginBottom: '8px' }}>
            Providing customer support and responding to inquiries
          </Typography>
          <Typography component="li" variant="body1" sx={{ marginBottom: '8px' }}>
            Monitoring compliance with our Acceptable Use Policy
          </Typography>
          <Typography component="li" variant="body1" sx={{ marginBottom: '8px' }}>
            Improving our services and developing new features
          </Typography>
        </Box>

        <Typography variant="h5" gutterBottom sx={{ marginTop: '30px' }}>
          Photo Usage and Sharing
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Within Your Club:</strong> Photos you upload may be used by club owners, contest administrators, and club members for legitimate club purposes, including:
        </Typography>
        <Box component="ul" sx={{ paddingLeft: '20px', marginBottom: '20px' }}>
          <Typography component="li" variant="body1">Creating club calendars, newsletters, and promotional materials</Typography>
          <Typography component="li" variant="body1">Displaying contest results and winner announcements</Typography>
          <Typography component="li" variant="body1">Club websites and social media accounts (with appropriate permissions)</Typography>
          <Typography component="li" variant="body1">Physical displays and presentations at club events</Typography>
        </Box>

        <Typography variant="body1" paragraph>
          <strong>Outside Your Club:</strong> Your photos will NOT be shared with external parties, other clubs, or used for commercial purposes without your explicit written permission.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ marginTop: '30px' }}>
          Snapperish Staff Access
        </Typography>
        <Typography variant="body1" paragraph>
          Snapperish staff may access your photos and account information only in the following limited circumstances:
        </Typography>
        <Box component="ul" sx={{ paddingLeft: '20px', marginBottom: '20px' }}>
          <Typography component="li" variant="body1" sx={{ marginBottom: '8px' }}>
            <strong>Policy Compliance:</strong> Reviewing photos for violations of our Acceptable Use Policy
          </Typography>
          <Typography component="li" variant="body1" sx={{ marginBottom: '8px' }}>
            <strong>Technical Support:</strong> Troubleshooting technical issues or providing customer assistance, limited to actions necessary to resolve the reported issue
          </Typography>
          <Typography component="li" variant="body1" sx={{ marginBottom: '8px' }}>
            <strong>Security Purposes:</strong> Investigating suspected abuse or fraudulent activity
          </Typography>
          <Typography component="li" variant="body1" sx={{ marginBottom: '8px' }}>
            <strong>Legal Requirements:</strong> Complying with legal obligations or court orders
          </Typography>
        </Box>

        <Typography variant="h5" gutterBottom sx={{ marginTop: '30px' }}>
          Data Storage and Security
        </Typography>
        <Typography variant="body1" paragraph>
          We implement appropriate technical and organizational measures to protect your personal information:
        </Typography>
        <Box component="ul" sx={{ paddingLeft: '20px', marginBottom: '20px' }}>
          <Typography component="li" variant="body1">Photos are stored securely with secure cloud storage providers with appropriate access controls</Typography>
          <Typography component="li" variant="body1">All data transmissions are encrypted using industry-standard protocols</Typography>
          <Typography component="li" variant="body1">Access to your data is limited to authorized personnel only</Typography>
          <Typography component="li" variant="body1">Regular security audits and updates are performed</Typography>
        </Box>

        <Typography variant="h5" gutterBottom sx={{ marginTop: '30px' }}>
          Your Rights and Choices
        </Typography>
        <Typography variant="body1" paragraph>
          You have the following rights regarding your personal information:
        </Typography>
        <Box component="ul" sx={{ paddingLeft: '20px', marginBottom: '20px' }}>
          <Typography component="li" variant="body1">Access and review your personal data</Typography>
          <Typography component="li" variant="body1">Request correction of inaccurate information</Typography>
          <Typography component="li" variant="body1">Delete your photos and account (subject to club record-keeping requirements)</Typography>
          <Typography component="li" variant="body1">Object to certain uses of your information</Typography>
          <Typography component="li" variant="body1">Request data portability where technically feasible</Typography>
        </Box>
        <Typography variant="body1" paragraph>
          <strong>California Residents:</strong> If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA), including the right to know what personal information we collect and how it is used, the right to request deletion of your personal information, and the right to non-discrimination for exercising your privacy rights. Snapperish does not sell personal information as defined under the CCPA. To submit a data access or deletion request, please contact us at{' '}
          <a href="mailto:matt+snapperish+privacy@fivepack.com" style={{ color: 'inherit' }}>matt+snapperish+privacy@fivepack.com</a>.
          {' '}We will respond to verified requests within 45 days.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ marginTop: '30px' }}>
          Data Retention
        </Typography>
        <Typography variant="body1" paragraph>
          We retain your information for as long as necessary to provide our services and comply with legal obligations:
        </Typography>
        <Box component="ul" sx={{ paddingLeft: '20px', marginBottom: '20px' }}>
          <Typography component="li" variant="body1">Photos and contest data are retained until removed by the club owner</Typography>
          <Typography component="li" variant="body1">Account information is kept until you request deletion or your account is terminated</Typography>
          <Typography component="li" variant="body1">
            <strong>Departed members:</strong> When a member is removed from a club or leaves a club, their account information and previously submitted photos are retained in the club's contest history by default, as club owners have a legitimate interest in maintaining complete contest records. A departed member may request deletion of their personal account information and photos by contacting us at{' '}
            <a href="mailto:matt+snapperish+privacy@fivepack.com" style={{ color: 'inherit' }}>matt+snapperish+privacy@fivepack.com</a>.
            {' '}Deletion requests will be honored within 30 days except where retention is required for legal compliance or active dispute resolution.
          </Typography>
          <Typography component="li" variant="body1">Some data may be retained longer for legal compliance or dispute resolution</Typography>
        </Box>

        <Typography variant="h5" gutterBottom sx={{ marginTop: '30px' }}>
          Third-Party Services
        </Typography>
        <Typography variant="body1" paragraph>
          Snapperish uses certain third-party services to operate our platform:
        </Typography>
        <Box component="ul" sx={{ paddingLeft: '20px', marginBottom: '20px' }}>
          <Typography component="li" variant="body1">Amazon Rekognition for Acceptable Use Policy determination and automated image scanning</Typography>
          <Typography component="li" variant="body1">Microsoft PhotoDNA for CSAM (child sexual abuse material) detection through image hash matching</Typography>
          <Typography component="li" variant="body1">Cloudflare for web infrastructure and data services</Typography>
          <Typography component="li" variant="body1">Stripe for subscription billing (Snapperish does not receive or retain any payment or credit card information)</Typography>
        </Box>
        <Typography variant="body1" paragraph>
          These services are bound by their own privacy policies and our contractual requirements for data protection.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ marginTop: '30px' }}>
          Changes to This Policy
        </Typography>
        <Typography variant="body1" paragraph>
          We may update this Privacy Policy periodically to reflect changes in our practices or legal requirements. Significant changes will be communicated to users via email or platform notifications.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ marginTop: '30px' }}>
          Contact Us
        </Typography>
        <Typography variant="body1" paragraph>
          If you have questions about this Privacy Policy or wish to exercise your rights, please{' '}
          <Link to="/contact" style={{ color: 'inherit', textDecoration: 'underline' }}>
            contact us
          </Link>{' '}
          or email Five Pack Creative, Inc. directly at{' '}
          <a href="mailto:matt+snapperish+privacy@fivepack.com" style={{ color: 'inherit' }}>matt+snapperish+privacy@fivepack.com</a>.
          Our mailing address is: Five Pack Creative, Inc., 7460 Warren Parkway, Suite 100, Frisco, TX 75034.
        </Typography>

        <Box sx={{
          textAlign: 'center',
          marginTop: '40px',
          padding: '20px',
          backgroundColor: (theme) => theme.palette.action.hover,
          borderRadius: '8px'
        }}>
          <Typography variant="body2" color="text.secondary">
            Last updated: April 2026
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ marginTop: '8px' }}>
            Questions about this policy? <Link to="/contact" style={{ color: 'inherit' }}>Contact us</Link>
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default Privacy;